<script setup>
import { ref } from 'vue';
import {
  CdrButton, CdrLink, CdrTooltip, IconXLg,
} from '@rei/cedar';

const props = defineProps({
  imageUrl: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  url: {
    type: String,
    default: '',
  },
});

// create a value to determine if the component should be rendered
const renderComponent = ref(!!props.imageUrl && !!props.name && !!props.url);

// set the component to not render
const hideComponent = () => { renderComponent.value = false; };

</script>

<template>
  <aside
    v-if="renderComponent"
    class="return-to-purchase"
    data-ui="return-to-purchase-path"
  >
    <cdr-link
      class="return-to-purchase__img-and-title"
      :href="url"
      modifier="standalone"
      :inherit-color="true"
      :data-analytics-id="`return_to_purchase_path_${name}`"
    >
      <span class="return-to-purchase__image-frame">
        <img
          class="return-to-purchase__image"
          :src="imageUrl"
          alt="name"
        >
      </span>
      <div
        class="return-to-purchase__title"
      >
        <div class="return-to-purchase__action-text">
          Keep Shopping
        </div>
        <span class="return-to-purchase__item-name">
          {{ name }}
        </span>
      </div>
    </cdr-link>
    <cdr-tooltip
      id="return-to-purchase__close-tooltip"
      position="top"
    >
      <template #trigger>
        <cdr-button
          class="return-to-purchase__close-button"
          data-ui="return-to-purchase__close-button"
          :icon-only="true"
          :data-analytics-id="`return_to_purchase_path_close_${name}`"
          @click="hideComponent"
        >
          <template #icon>
            <icon-x-lg
              class="return-to-purchase__close-icon"
              alt="Close"
            />
          </template>
        </cdr-button>
      </template>
      <div>
        Close
      </div>
    </cdr-tooltip>
  </aside>
</template>

<style scoped lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens';

.return-to-purchase {
  display: flex;
  justify-content: space-between;
  gap: $cdr-space-inset-two-x;
  background-color: #fff;
  height: 104px;
  border: solid 1px #dcd6cb;
  box-shadow: 0 0 4px #00000040;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  padding: $cdr-space-inset-one-x;

  @include cdr-sm-mq-up {
    height: 80px;
    padding-left: 5.6rem;
  }
}

.return-to-purchase__img-and-title {
    display: flex;
    align-items: center;
    gap: $cdr-space-inset-one-x;
}

.return-to-purchase__image-frame {
  min-width: 72px;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.return-to-purchase__image {
  max-height: 72px;
  max-width: 72px;
}

.return-to-purchase__title {
  @include cdr-text-utility-serif-200;

  @include cdr-sm-mq-up {
    @include cdr-text-utility-serif-300;
  }
}

.return-to-purchase__action-text {
  @include cdr-text-eyebrow-100;
}

.return-to-purchase__item-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  @include cdr-sm-mq-up {
    display: revert;
    -webkit-box-orient: revert;
    -webkit-line-clamp: revert;
    overflow: revert;
  }
}

.return-to-purchase__close-button {
  padding: 0;
}

.return-to-purchase__close-icon {
  position: relative;
  align-self: start;
}

</style>
